/**
 * Formulaire de modification d'une campagne existante, partagé entre portails
 * (portails admin et client).
 *
 * Ne sont éditables que les champs que `campagnes.modifierCampagne` accepte :
 * nom, statut, dates, libre-service et son mot de passe. Les épreuves, le type
 * de parcours, le mode anonyme, l'organisation et le groupe sont figés à la
 * création — les afficher en lecture seule évite de laisser croire l'inverse.
 * L'organisation et le groupe ne sont rappelés qu'au portail admin
 * (`afficherOrganisationFigee`) : le client n'a pas à s'en soucier.
 *
 * L'autorisation est côté serveur : `modifierCampagne` exige un super-admin ou
 * un user qui voit la campagne dans son org/groupe. Ce composant ne rejoue pas
 * ce contrôle.
 */
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { useMutation } from "convex/react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import {
  APPLICATIONS,
  applicationDe,
  getTypeEvalLabel,
  libelleApplication,
} from "@repo/utils";
import { api } from "../../../convex/_generated/api";
import type { Doc } from "../../../convex/_generated/dataModel";
import { StatutCampagneEnum } from "../../../convex/schema";

/** Sous-ensemble de la campagne dont le formulaire a besoin. */
export type CampagneAModifier = Pick<
  Doc<"campagnes">,
  | "_id"
  | "nom"
  | "statut"
  | "dateDebut"
  | "dateFin"
  | "evaluationsConfig"
  | "anonyme"
  | "autoriserCreationParcoursCandidats"
>;

export interface CampagneEditFormProps {
  campagne: CampagneAModifier;
  retourHref: string;
  retourLabel: string;
  /** Où aller après un enregistrement réussi (par défaut `retourHref`). */
  succesHref?: string;
  /** Rappeler que l'organisation et le groupe sont figés (portail admin). */
  afficherOrganisationFigee?: boolean;
}

/** `Date.now()` → `yyyy-mm-dd` pour un `<input type="date">`, en local. */
function versInputDate(ts: number | undefined): string {
  if (!ts) return "";
  const d = new Date(ts);
  const mois = String(d.getMonth() + 1).padStart(2, "0");
  const jour = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mois}-${jour}`;
}

/**
 * « PAMI », « Inbasket — rapide », « FairView — assistant-manager » : le
 * template n'est précisé que pour une application qui le fait choisir à la
 * création (variante, scorecard).
 */
function libelleEpreuveFigee(e: CampagneAModifier["evaluationsConfig"][number]) {
  const cle = applicationDe(e);
  if (!cle) return `${getTypeEvalLabel(e.typeEval)}${e.template ? ` — ${e.template}` : ""}`;
  const choisi = APPLICATIONS.find((a) => a.cle === cle)?.choixALaCreation;
  return `${libelleApplication(cle)}${choisi && e.template ? ` — ${e.template}` : ""}`;
}

const STATUTS = [
  { value: StatutCampagneEnum.BROUILLON, label: "Brouillon" },
  { value: StatutCampagneEnum.ACTIVE, label: "Active" },
  { value: StatutCampagneEnum.TERMINEE, label: "Terminée" },
] as const;

export function CampagneEditForm({
  campagne,
  retourHref,
  retourLabel,
  succesHref,
  afficherOrganisationFigee = false,
}: CampagneEditFormProps) {
  const modifierCampagne = useMutation(api.campagnes.modifierCampagne);
  const navigate = useNavigate();

  const [nom, setNom] = useState(campagne.nom);
  const [statut, setStatut] = useState<StatutCampagneEnum>(campagne.statut);
  const [dateDebut, setDateDebut] = useState(versInputDate(campagne.dateDebut));
  const [dateFin, setDateFin] = useState(versInputDate(campagne.dateFin));
  const [autoriserCreation, setAutoriserCreation] = useState(
    campagne.autoriserCreationParcoursCandidats ?? false,
  );
  const [motDePasse, setMotDePasse] = useState("");
  const [motDePasseConfirm, setMotDePasseConfirm] = useState("");
  const [showMotDePasse, setShowMotDePasse] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // La campagne arrive par une query réactive : le premier rendu peut précéder
  // son chargement, et une modification concurrente doit se refléter ici.
  useEffect(() => {
    setNom(campagne.nom);
    setStatut(campagne.statut);
    setDateDebut(versInputDate(campagne.dateDebut));
    setDateFin(versInputDate(campagne.dateFin));
    setAutoriserCreation(campagne.autoriserCreationParcoursCandidats ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campagne._id]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (autoriserCreation && motDePasse !== motDePasseConfirm) {
        throw new Error("Les mots de passe ne correspondent pas.");
      }
      await modifierCampagne({
        campagneId: campagne._id,
        nom,
        statut,
        dateDebut: dateDebut ? new Date(dateDebut).getTime() : undefined,
        dateFin: dateFin ? new Date(dateFin).getTime() : undefined,
        autoriserCreationParcoursCandidats: autoriserCreation,
        // Champ laissé vide = mot de passe inchangé (on n'envoie rien). Décocher
        // le libre-service efface le mot de passe existant, via `""`.
        motDePasseCampagne: !autoriserCreation
          ? ""
          : motDePasse
            ? motDePasse
            : undefined,
      });

      void navigate(succesHref ?? retourHref);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid gap-6">
      <div className="mb-0">
        <Link
          to={retourHref}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← {retourLabel}
        </Link>
      </div>

      {error && (
        <Card className="border-etat-erreur">
          <CardHeader>
            <CardTitle className="text-etat-erreur">Erreur</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-sm text-etat-erreur whitespace-pre-wrap">
              {error}
            </pre>
          </CardContent>
        </Card>
      )}

      <Card className="min-w-112.5">
        <CardHeader>
          <CardTitle>Modifier la campagne</CardTitle>
          <CardDescription>
            Les champs figés à la création sont rappelés en bas de page.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="nom">Nom de la campagne</Label>
              <Input
                id="nom"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                required
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="statut">Statut</Label>
              <Select
                value={statut}
                onValueChange={(v) => setStatut(v as StatutCampagneEnum)}
              >
                <SelectTrigger id="statut">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUTS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="dateDebut">Date de début</Label>
                <Input
                  id="dateDebut"
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="dateFin">Date de fin</Label>
                <Input
                  id="dateFin"
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-3 rounded-surface border p-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="autoriserCreation"
                  checked={autoriserCreation}
                  onCheckedChange={(coche) => {
                    const actif = coche === true;
                    setAutoriserCreation(actif);
                    if (!actif) {
                      setMotDePasse("");
                      setMotDePasseConfirm("");
                    }
                  }}
                />
                <Label htmlFor="autoriserCreation">
                  Autoriser la création de parcours par les candidats
                </Label>
              </div>

              {autoriserCreation && (
                <>
                  <div className="grid gap-2 pl-6">
                    <Label htmlFor="motDePasseCampagne">
                      Nouveau mot de passe d&apos;accès
                    </Label>
                    <div className="relative">
                      <Input
                        id="motDePasseCampagne"
                        type={showMotDePasse ? "text" : "password"}
                        value={motDePasse}
                        onChange={(e) => setMotDePasse(e.target.value)}
                        placeholder="Laisser vide pour conserver l'actuel"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowMotDePasse((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        tabIndex={-1}
                      >
                        {showMotDePasse ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>
                  {motDePasse && (
                    <div className="grid gap-2 pl-6">
                      <Label htmlFor="motDePasseConfirm">
                        Confirmer le mot de passe
                      </Label>
                      <Input
                        id="motDePasseConfirm"
                        type={showMotDePasse ? "text" : "password"}
                        value={motDePasseConfirm}
                        onChange={(e) => setMotDePasseConfirm(e.target.value)}
                        required
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Figé à la création, en dernier : changer un template laisserait
                les passations déjà créées sur l'ancien, donc une campagne aux
                résultats non comparables ; les épreuves et le mode anonyme
                sont structurants de la même façon. Le bon geste est de créer
                une autre campagne. */}
            <div className="grid gap-3 rounded-surface border border-dashed p-4">
              <h4 className="text-sm font-medium text-muted-foreground">
                Non modifiable (figé à la création)
              </h4>
              <div className="grid gap-1 text-sm text-muted-foreground">
                <div>
                  Mode anonyme : {campagne.anonyme ? "oui" : "non"}
                </div>
              </div>
              <div className="grid gap-1 text-sm text-muted-foreground">
                <span>Épreuves :</span>
                <ul className="list-disc pl-5">
                  {campagne.evaluationsConfig.map((e, index) => (
                    <li key={index}>{libelleEpreuveFigee(e)}</li>
                  ))}
                </ul>
              </div>
              {afficherOrganisationFigee && (
                <p className="text-xs text-muted-foreground">
                  L&apos;organisation et le groupe le sont également.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button type="submit" disabled={isLoading} className="mt-2">
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  "Enregistrer"
                )}
              </Button>
              <Link to={retourHref} className="mt-2">
                <Button type="button" variant="outline">
                  Annuler
                </Button>
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
